import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { FileDropZone } from '@/components/shared/FileDropZone';
import { showError } from '@/lib/toastHelpers';

const sections = [
  { value: 'profile', label: 'Profile' },
  { value: 'user-settings', label: 'User Settings' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'my-recipes', label: 'My Recipes' },
  { value: 'planner', label: 'Planner' },
  { value: 'profit-goal', label: 'Profit Goal' },
  { value: 'menus', label: 'Menus' },
  { value: 'pantry', label: 'Pantry' }
];

interface MediaUploadFormProps {
  onUpload: (formData: FormData) => void;
  isLoading?: boolean;
}

export function MediaUploadForm({ onUpload, isLoading }: MediaUploadFormProps) {
  const [form, setForm] = useState({
    section: 'profile',
    mediaType: 'image',
    title: '',
    description: '',
    order: 0,
    file: null as File | null
  });

  const handleSubmit = () => {
    if (!form.file || !form.title) {
      showError('Please provide a file and title');
      return;
    }

    const formData = new FormData();
    formData.append('file', form.file);
    formData.append('section', form.section);
    formData.append('mediaType', form.mediaType);
    formData.append('title', form.title);
    formData.append('description', form.description);
    formData.append('order', form.order.toString());

    onUpload(formData);
    
    // Reset form
    setForm({
      section: 'profile',
      mediaType: 'image',
      title: '',
      description: '',
      order: 0,
      file: null
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload New Media</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Section</Label>
            <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sections.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Media Type</Label>
            <Select value={form.mediaType} onValueChange={(v) => setForm({ ...form, mediaType: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="image">Image</SelectItem>
                <SelectItem value="video">Video</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Title</Label>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g., Dashboard overview"
          />
        </div>

        <div>
          <Label>Description (Optional)</Label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Brief description of the media"
          />
        </div>

        <div>
          <Label>Display Order</Label>
          <Input
            type="number"
            value={form.order}
            onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
          />
        </div>

        <div>
          <Label>File</Label>
          <FileDropZone file={form.file} onChange={(f) => setForm({ ...form, file: f })} />
        </div>

        <Button onClick={handleSubmit} disabled={isLoading}>Upload Media</Button>
      </CardContent>
    </Card>
  );
}
